import React from 'react';
import { useParams } from 'react-router-dom';
import { CheckIcon, ClockIcon, GaugeIcon, PackageIcon, ShieldCheckIcon } from 'lucide-react';
import { Seo } from '../components/ui/Seo';
import { PageHero } from '../components/ui/PageHero';
import { ButtonLink } from '../components/ui/ButtonLink';
import { CourseCard } from '../components/courses/CourseCard';
import { BookingSidebar } from '../components/courses/BookingSidebar';
import { ReviewCard } from '../components/shared/ReviewCard';
import { reviews } from '../data/reviews';
import { BOOKING_URL } from '../data/site';
import { getCourse, priceLabel } from '../utils/courses';
import { NotFound } from './NotFound';
export function CourseDetail() {
  const {
    slug = ''
  } = useParams();
  const course = getCourse(slug);
  if (!course) return <NotFound data-id="element-732" data-mp="1rrmo4mrmlwh0" />;
  const related = course.related.map(getCourse).filter((c): c is NonNullable<typeof c> => Boolean(c));
  return <>
      <Seo title={`${course.name} (${course.code})`} description={`${course.benefit} ${course.duration}, ${priceLabel(course)}. Las Vegas, NV.`} data-id="element-733" data-mp="1mt310j1aqen2" />
      <PageHero title={course.name} sub={course.benefit} image={course.image} badge={course.code} crumbs={[{
      label: 'Courses',
      to: '/courses'
    }, {
      label: course.code
    }]} data-id="element-734" data-mp="2d2m0sqwehvd6">
        <ul className="flex flex-wrap gap-x-6 gap-y-2 text-chrome-100" data-id="element-735" data-mp="2n09wbj1tz6we">
          <li className="flex items-center gap-2" data-id="element-736" data-mp="1pdugelp1yhay">
            <ClockIcon className="h-4 w-4 text-chrome-300" aria-hidden="true" data-id="element-737" data-mp="1pf5fi87jz0vq" /> {course.duration}
          </li>
          <li className="flex items-center gap-2" data-id="element-738" data-mp="1uaktr19h2yso">
            <GaugeIcon className="h-4 w-4 text-chrome-300" aria-hidden="true" data-id="element-739" data-mp="1zbgvb86cq8ux" /> {course.level}
          </li>
          <li className="font-semibold text-white" data-id="element-740" data-mp="243ifrhdob5w1">{priceLabel(course)}</li>
        </ul>
      </PageHero>

      <div className="gutter section grid gap-12 bg-mist lg:grid-cols-12 lg:gap-14" data-id="element-741" data-mp="16m9zigya7xdd">
        <div className="space-y-16 lg:col-span-8 2xl:col-span-9" data-id="element-742" data-mp="1ble7535k5n1l">
          <section aria-labelledby="overview-title" data-id="element-743" data-mp="13lgjq7qkoyxk">
            <h2 id="overview-title" className="t-h3 text-navy" data-id="element-744" data-mp="13jx4mkgnktxd">Overview</h2>
            <span className="red-rule mt-3" aria-hidden="true" data-id="element-745" data-mp="zkoahehpau1a" />
            <p className="t-lead mt-5 max-w-[60rem] text-ink" data-id="element-746" data-mp="1db9vp9umng2c">{course.overview}</p>
          </section>

          <section aria-labelledby="learn-title" data-id="element-747" data-mp="18c6obir6cxr7">
            <h2 id="learn-title" className="t-h3 text-navy" data-id="element-748" data-mp="18dhwdurnkro4">What you’ll learn</h2>
            <span className="red-rule mt-3" aria-hidden="true" data-id="element-749" data-mp="knniraziez2t" />
            <ul className="mt-6 grid gap-x-10 gap-y-3 md:grid-cols-2 3xl:grid-cols-3" data-id="element-750" data-mp="gl0ol3fd922g">
              {course.learn.map(item => <li key={item} className="flex gap-3 border-b border-navy/10 pb-3 text-[16px] text-ink" data-id="element-751" data-mp="ulthjjq0rp3g">
                  <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden="true" data-id="element-752" data-mp="pkxfzct54f17" />
                  {item}
                </li>)}
            </ul>
          </section>

          <section aria-labelledby="schedule-title" className="grid gap-8 md:grid-cols-2" data-id="element-753" data-mp="20zsv0zvp8iia">
            <div data-id="element-754" data-mp="3iy7iic432y5u">
              <h2 id="schedule-title" className="t-h3 text-navy" data-id="element-755" data-mp="3ixpdapzg19ls">Duration &amp; schedule</h2>
              <span className="red-rule mt-3" aria-hidden="true" data-id="element-756" data-mp="3nyd71jf244jp" />
              <p className="mt-5 font-display text-3xl font-semibold uppercase text-navy" data-id="element-757" data-mp="39yc5s2uvqv3p">{course.duration}</p>
              <p className="mt-2 text-steel" data-id="element-758" data-mp="3ew3xavrm4f7d">{course.schedule}</p>
            </div>
            <div data-id="element-759" data-mp="6hc5ho77zoqi">
              <h2 className="t-h3 text-navy" data-id="element-760" data-mp="6gtypxmxls4c">Tuition</h2>
              <span className="red-rule mt-3" aria-hidden="true" data-id="element-761" data-mp="6jg69x9to7mk" />
              <p className="mt-5 font-display text-3xl font-semibold text-navy" data-id="element-762" data-mp="ai5rud50kvha">{priceLabel(course)}</p>
              <p className="mt-2 flex items-center gap-2 text-steel" data-id="element-763" data-mp="3sw55yxe4cqxe">
                <PackageIcon className="h-4 w-4" aria-hidden="true" data-id="element-764" data-mp="1pbwz7d33nib" /> Save when you bundle in a training package.
              </p>
            </div>
          </section>

          <section aria-label="What to bring and what we provide" className="grid gap-5 md:grid-cols-2" data-id="element-765" data-mp="u6ocqiizlmq6">
            {[{
            title: 'What to bring',
            items: course.bring
          }, {
            title: 'What we provide',
            items: course.provide
          }].map(col => <div key={col.title} className="rounded-[4px] bg-white p-6 ring-1 ring-navy/10 lg:p-8" data-id="element-766" data-mp="2wthmbeai57wd">
                <h2 className="t-h3 text-navy" data-id="element-767" data-mp="2sre5sb8z7d48">{col.title}</h2>
                <ul className="mt-5 space-y-3" data-id="element-768" data-mp="2ns19z104mvpi">
                  {col.items.map(i => <li key={i} className="flex gap-3 text-ink" data-id="element-769" data-mp="3kffq0x1vcqn0">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" data-id="element-770" data-mp="3gem0y0cox5sj" />
                      {i}
                    </li>)}
                </ul>
              </div>)}
          </section>

          <section aria-labelledby="prereq-title" className="flex gap-4 rounded-[4px] border-l-4 border-navy bg-white p-6" data-id="element-771" data-mp="36p2i2uoz9bxo">
            <ShieldCheckIcon className="mt-0.5 h-6 w-6 shrink-0 text-navy" aria-hidden="true" data-id="element-772" data-mp="38lyjtf8enwx" />
            <div data-id="element-773" data-mp="36resifp51uf">
              <h2 id="prereq-title" className="font-display text-lg font-semibold uppercase text-navy" data-id="element-774" data-mp="ak0e93snebtt">Prerequisites</h2>
              <ul className="mt-2 space-y-1 text-ink" data-id="element-775" data-mp="fjwfwwdvhr67">
                {course.prerequisites.map(p => <li key={p} data-id="element-776" data-mp="kh5012il05a3">{p}</li>)}
              </ul>
            </div>
          </section>

          <section aria-labelledby="course-reviews-title" data-id="element-777" data-mp="ol30phnk1o59">
            <h2 id="course-reviews-title" className="t-h3 text-navy" data-id="element-778" data-mp="tj4htx1f7de3">What students say</h2>
            <span className="red-rule mt-3" aria-hidden="true" data-id="element-779" data-mp="yhfu0ef6tzzh" />
            <ul className="mt-6 grid gap-5 md:grid-cols-2 2xl:grid-cols-3" data-id="element-780" data-mp="yfmj2mxjktlo">
              {reviews.map(r => <li key={r.id} data-id="element-781" data-mp="13as5dmuuwaaa">
                  <ReviewCard review={r} data-id="element-782" data-mp="187sa9ufyz6zt" />
                </li>)}
            </ul>
          </section>
        </div>

        <div className="hidden lg:col-span-4 lg:block 2xl:col-span-3" data-id="element-783" data-mp="1caoxxzkd9tam">
          <div className="sticky top-[calc(var(--header-h)+1.5rem)]" data-id="element-784" data-mp="1h7fe0mw68ow1">
            <BookingSidebar course={course} data-id="element-785" data-mp="3dbbokdvozafg" />
          </div>
        </div>
      </div>

      {related.length > 0 && <section className="gutter section bg-white" aria-labelledby="related-title" data-id="element-786" data-mp="39a9x4j80o2o1">
          <h2 id="related-title" className="t-h2 text-navy" data-id="element-787" data-mp="3n0v87lr8b8bd">Related courses</h2>
          <span className="red-rule mt-5" aria-hidden="true" data-id="element-788" data-mp="3n2zmmxmr7ppw" />
          <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-4" data-id="element-789" data-mp="3i9n0cu5jrv0b">
            {related.map(c => <li key={c.slug} data-id="element-790" data-mp="w1bjv3wwc5d">
                <CourseCard course={c} data-id="element-791" data-mp="3s0rbtjw0kxqe" />
              </li>)}
          </ul>
        </section>}

      <div className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-4 border-t border-white/10 bg-ink px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] lg:hidden" data-id="element-792" data-mp="9uv49ae4gidn">
        <div className="min-w-0" data-id="element-793" data-mp="2blgecqubgwkh">
          <p className="truncate text-xs text-chrome-300" data-id="element-794" data-mp="2bmhxzjpeerdt">{course.code} · {course.duration}</p>
          <p className="font-display text-2xl font-bold text-white" data-id="element-795" data-mp="26og4s5y9dbom">{priceLabel(course)}</p>
        </div>
        <ButtonLink href={BOOKING_URL} className="shrink-0" data-id="element-796" data-mp="m5e1byom9aby">
          Book Now
        </ButtonLink>
      </div>
    </>;
}